import { useCallback } from 'react'
import { useUserState } from '~/atoms/UserState'
import { checkRole, CheckRoleFn } from '~/utils/auth'

/**
 * Returns a role-check function bound to the current signed-in user.
 *
 * Accepted role levels:
 * - Portal: `'limited' | 'restricted' | 'unrestricted'`
 * - Administrator: `'admin' | 'senior' | 'super'`
 *
 * @returns A function with signature:
 * `(requiredRole) => boolean`
 *
 * @example
 * // Basic usage
 * const checkRole = useCheckRole()
 * const canManageSwitchboard = checkRole('admin')
 *
 * @example
 * // Gate a component
 * const checkRole = useCheckRole()
 * if (!checkRole('admin')) return null
 * return <SwitchboardComponent />
 *
 * @example
 * // Disable a destructive action for non-senior users
 * const checkRole = useCheckRole()
 * const canDelete = checkRole('senior')
 * <Button disabled={!canDelete}>Delete</Button>
 *
 * @remarks
 * Mango staff (`is_staff`) and superusers (`is_superuser`) always return `true`.
 * https://support.mangovoice.com/user-roles-and-permissions/
 */
export const useCheckRole = (): CheckRoleFn => {
  const { userRoles } = useUserState({ canAutoRefresh: false })

  return useCallback(
    (requiredRole) => checkRole(userRoles, requiredRole),
    [userRoles],
  )
}
